import { Square2StackIcon } from '@heroicons/react/20/solid'
import { AxiosError } from 'axios'
import { FC } from 'react'

import { RequestByIdApiType } from '@/api/models/requests'
import { ServerValidationError } from '@/api/shared/types'
import { cn } from '@/app/utils'
import { PageType } from '@/components/entities/requests/model/types'
import { formatRequestDate } from '@/components/entities/requests/ui/RequestDetails/Content/lib/utils'
import { AppCopyToClipboard } from '@/components/shared/copy-to-clipboard'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'

import { ContentBlockPayment } from './ContentBlockPayment'
import ContentBlockRemainingTime from './ContentBlockRemainingTime'
import { ContentBlockRequestRating } from './RequestRating/ContentBlockRequestRating'

type Props = {
  request: RequestByIdApiType
  pageType: PageType
  onPayClick: () => void
  refetchRequest: () => void
  onFormError: (error: AxiosError<ServerValidationError>) => void
}

export const ContentHeader: FC<Props> = ({
  request,
  pageType,
  onPayClick,
  refetchRequest,
  onFormError
}) => {
  const { shouldShowComments } = useRequestNavigation()

  return (
    <div className={shouldShowComments ? 'mt-20 lg:mt-0' : ''}>
      <div className="mb-24 flex items-center justify-between px-0 last:mb-0">
        <div
          className={cn(
            'flex w-full flex-row rounded-t-16 bg-grey-20 p-24 sm:p-16 sm:pb-24 xxs:flex-col xxs:gap-12',
            shouldShowComments && 'rounded-16 bg-grey-10',
            (request.canBePaid || request.canBeRated) && 'justify-between'
          )}
        >
          <div className="flex flex-wrap gap-x-[120px] gap-y-12 xxs:gap-x-[95px]">
            <div
              data-testid="request-details-created-at-id"
              className="flex flex-col gap-4"
            >
              <span className="font-bold text-16">Создано</span>
              <span className="font-normal text-16">
                {formatRequestDate(request.createdAt)}
              </span>
            </div>
            <div
              data-testid="request-details-number-id"
              className="flex flex-col gap-4"
            >
              <div className="flex items-center gap-4">
                <span className="whitespace-nowrap font-bold text-16">
                  № {pageType === PageType.Passes ? 'пропуска' : 'обращения'}
                </span>
                <Square2StackIcon />
              </div>
              <span className="flex items-center gap-4 font-normal text-16">
                {request.number}{' '}
                <AppCopyToClipboard
                  text={String(request.number)}
                  message={`Номер ${
                    pageType === PageType.Passes ? 'пропуска' : 'обращения'
                  } скопирован`}
                />
              </span>
            </div>
            <ContentBlockRemainingTime
              statusAlias={request.statusAlias}
              desiredDateStart={request.desiredDateStart}
              desiredDateEnd={request.desiredDateEnd}
            />
          </div>
          <ContentBlockRequestRating
            canBeRated={request.canBeRated}
            requestType={request.typeAlias}
            id={request.id}
            isButtonUnsolvedAvailable={request.isButtonUnsolvedAvailable}
            refetchRequest={refetchRequest}
          />
          {request.canBePaid && (
            <ContentBlockPayment
              invoiceId={request.invoiceId}
              onPayClick={onPayClick}
              requestType={request.typeAlias}
              onFormError={onFormError}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ContentHeader
